import React, { useMemo, useState } from 'react';
import {
  IonAlert,
  IonButton,
  IonContent,
  IonDatetime,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateAssetDto, IAsset, UpdateAssetDto } from 'shared';
import apiClient from '../api/client';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { useLanguage } from '../context/LanguageContext';
import MetricCard from '../components/MetricCard';
import { formatCurrency, formatDateTime } from '../utils/format';
import { RefresherEvent } from '../types/ionic';

interface AssetFormState {
  id?: string;
  name: string;
  value: string;
  yearsOfUse: string;
  purchaseDate: string;
}

const defaultFormState: AssetFormState = {
  name: '',
  value: '',
  yearsOfUse: '',
  purchaseDate: new Date().toISOString().split('T')[0],
};

const Assets: React.FC = () => {
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const [presentToast] = useIonToast();
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState<AssetFormState>(defaultFormState);
  const [showFormModal, setShowFormModal] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<IAsset | null>(null);

  const assetsQuery = useQuery({
    queryKey: ['assets'],
    queryFn: async () => {
      const { data } = await apiClient.get<IAsset[]>('/assets');
      return data;
    },
  });

  const createAssetMutation = useMutation({
    mutationFn: async (payload: CreateAssetDto) => {
      const { data } = await apiClient.post<IAsset>('/assets', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      presentToast({ message: t('assetCreatedSuccessfully') ?? 'Asset created', duration: 2000, color: 'success' });
      closeForm();
    },
    onError: () => {
      presentToast({ message: t('failedToCreateAsset') ?? 'Failed to create asset', duration: 2000, color: 'danger' });
    },
  });

  const updateAssetMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAssetDto }) => {
      const { data: response } = await apiClient.put<IAsset>(`/assets/${id}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      presentToast({ message: t('assetUpdatedSuccessfully') ?? 'Asset updated', duration: 2000, color: 'success' });
      closeForm();
    },
    onError: () => {
      presentToast({ message: t('failedToUpdateAsset') ?? 'Failed to update asset', duration: 2000, color: 'danger' });
    },
  });

  const deleteAssetMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/assets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      presentToast({ message: t('assetDeletedSuccessfully') ?? 'Asset deleted', duration: 2000, color: 'success' });
      setSelectedAsset(null);
    },
    onError: () => {
      presentToast({ message: t('failedToDeleteAsset') ?? 'Failed to delete asset', duration: 2000, color: 'danger' });
    },
  });

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await assetsQuery.refetch();
    } finally {
      event.detail.complete();
    }
  };

  const openCreateModal = () => {
    setFormState(defaultFormState);
    setMode('create');
    setShowFormModal(true);
  };

  const openEditModal = (asset: IAsset) => {
    setMode('edit');
    const purchaseDate = asset.purchaseDate ? new Date(asset.purchaseDate) : new Date();
    const isoDate = Number.isNaN(purchaseDate.getTime())
      ? new Date().toISOString().split('T')[0]
      : purchaseDate.toISOString().split('T')[0];
    setFormState({
      id: asset.id,
      name: asset.name,
      value: String(asset.value ?? ''),
      yearsOfUse: String(asset.yearsOfUse ?? ''),
      purchaseDate: isoDate,
    });
    setShowFormModal(true);
  };

  const closeForm = () => {
    if (createAssetMutation.isPending || updateAssetMutation.isPending) {
      return;
    }
    setShowFormModal(false);
    setFormState(defaultFormState);
  };

  const submitForm = () => {
    if (!formState.name.trim()) {
      presentToast({ message: t('enterAssetName') ?? 'Enter asset name', duration: 2000, color: 'warning' });
      return;
    }
    const value = Number(formState.value);
    const years = Number(formState.yearsOfUse);
    if (Number.isNaN(value) || value <= 0) {
      presentToast({ message: t('enterValidValue') ?? 'Enter valid value', duration: 2000, color: 'warning' });
      return;
    }
    if (Number.isNaN(years) || years <= 0) {
      presentToast({ message: t('enterValidYears') ?? 'Enter years of use', duration: 2000, color: 'warning' });
      return;
    }

    const payload: CreateAssetDto = {
      name: formState.name.trim(),
      value,
      yearsOfUse: years,
      purchaseDate: formState.purchaseDate,
    };

    if (mode === 'create') {
      createAssetMutation.mutate(payload);
    } else if (formState.id) {
      updateAssetMutation.mutate({ id: formState.id, data: payload });
    }
  };

  const assets = assetsQuery.data ?? [];
  const metrics = useMemo(() => {
    if (assets.length === 0) {
      return {
        totalAssets: 0,
        totalValue: 0,
        totalConsumption: 0,
        averageDepreciation: 0,
      };
    }
    const totalAssets = assets.length;
    const totalValue = assets.reduce((sum, asset) => sum + Number(asset.value || 0), 0);
    const totalConsumption = assets.reduce((sum, asset) => sum + Number(asset.consumptionCost || 0), 0);
    const averageDepreciation = totalAssets > 0 ? totalConsumption / totalAssets : 0;
    return { totalAssets, totalValue, totalConsumption, averageDepreciation };
  }, [assets]);

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('assets')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <section style={{ marginBottom: 16 }}>
            <MetricCard
              title={t('totalAssets') ?? 'Total assets'}
              value={`${metrics.totalAssets}`}
              context={`${t('totalValue') ?? 'Value'}: ${formatCurrency(metrics.totalValue, locale)}`}
              accent="primary"
            />
            <MetricCard
              title={t('totalConsumption') ?? 'Consumption cost'}
              value={formatCurrency(metrics.totalConsumption, locale)}
              context={`${t('averageDepreciation') ?? 'Avg depreciation'}: ${formatCurrency(metrics.averageDepreciation, locale)}`}
              accent="warning"
            />
          </section>

          <IonButton expand="block" onClick={openCreateModal} style={{ marginBottom: 16 }}>
            {t('addAsset') ?? 'Add asset'}
          </IonButton>

          {assetsQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : assets.length === 0 ? (
            <IonText color="medium">
              <p>{t('noData')}</p>
            </IonText>
          ) : (
            <IonList>
              {assets.map((asset) => (
                <IonItem key={asset.id} button detail onClick={() => setSelectedAsset(asset)}>
                  <IonLabel>
                    <h2 style={{ fontWeight: 600 }}>{asset.name}</h2>
                    <p>
                    {t('totalValue')}: {formatCurrency(asset.value ?? 0, locale)} • {t('purchaseDate') ?? 'Purchase'}:{' '}
                      {formatDateTime(asset.purchaseDate, 'dd MMM yyyy')}
                    </p>
                  </IonLabel>
                  <IonText slot="end" color="primary">
                    <strong>{formatCurrency(asset.consumptionCost ?? 0, locale)}</strong>
                  </IonText>
                </IonItem>
              ))}
            </IonList>
          )}
        </div>

        <IonModal isOpen={showFormModal} onDidDismiss={closeForm}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {mode === 'create' ? t('addAsset') ?? 'Add asset' : t('editAsset') ?? 'Edit asset'}
              </IonTitle>
              <IonButton slot="end" fill="clear" onClick={closeForm}>
                {t('close')}
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">{t('name')}</IonLabel>
                <IonInput
                  value={formState.name}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, name: event.detail.value ?? '' }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('value') ?? 'Value'}</IonLabel>
                <IonInput
                  type="number"
                  value={formState.value}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, value: event.detail.value ?? '' }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('yearsOfUse') ?? 'Years of use'}</IonLabel>
                <IonInput
                  type="number"
                  value={formState.yearsOfUse}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, yearsOfUse: event.detail.value ?? '' }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('purchaseDate') ?? 'Purchase date'}</IonLabel>
                <IonDatetime
                  presentation="date"
                  value={formState.purchaseDate}
                  onIonChange={(event) => {
                    const value = event.detail.value;
                    if (value) {
                      const normalized = Array.isArray(value) ? value[0] : value;
                      setFormState((prev) => ({ ...prev, purchaseDate: normalized }));
                    }
                  }}
                />
              </IonItem>
            </IonList>
            <IonButton
              expand="block"
              style={{ marginTop: 16 }}
              onClick={submitForm}
              disabled={createAssetMutation.isPending || updateAssetMutation.isPending}
            >
              {mode === 'create' ? t('save') : t('update')}
            </IonButton>
          </IonContent>
        </IonModal>

        <IonModal isOpen={Boolean(selectedAsset)} onDidDismiss={() => setSelectedAsset(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{selectedAsset?.name ?? t('assets')}</IonTitle>
              <IonButton slot="end" fill="clear" onClick={() => setSelectedAsset(null)}>
                {t('close')}
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedAsset ? (
              <>
                <IonText color="medium">
                  <p>
                    {t('value') ?? 'Value'}: {formatCurrency(selectedAsset.value ?? 0, locale)}
                  </p>
                  <p>
                    {t('yearsOfUse') ?? 'Years of use'}: {selectedAsset.yearsOfUse}
                  </p>
                  <p>
                    {t('consumptionCost') ?? 'Consumption cost'}: {formatCurrency(selectedAsset.consumptionCost ?? 0, locale)}
                  </p>
                  <p>
                    {t('purchaseDate') ?? 'Purchase date'}: {formatDateTime(selectedAsset.purchaseDate, 'dd MMM yyyy')}
                  </p>
                </IonText>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <IonButton expand="block" onClick={() => openEditModal(selectedAsset)}>
                    {t('edit')}
                  </IonButton>
                  <IonButton
                    expand="block"
                    color="danger"
                    onClick={() => setShowDeleteAlert(true)}
                    disabled={deleteAssetMutation.isPending}
                  >
                    {t('delete')}
                  </IonButton>
                </div>
              </>
            ) : null}
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={showDeleteAlert}
          header={t('confirmDeleteAsset') ?? 'Delete asset?'}
          message={t('deleteAssetMessage') ?? 'This action cannot be undone.'}
          buttons={[
            {
              text: t('cancel'),
              role: 'cancel',
              handler: () => setShowDeleteAlert(false),
            },
            {
              text: t('delete'),
              role: 'destructive',
              handler: () => {
                if (selectedAsset) {
                  deleteAssetMutation.mutate(selectedAsset.id);
                }
                setShowDeleteAlert(false);
              },
            },
          ]}
          onDidDismiss={() => setShowDeleteAlert(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default Assets;
